import { apiClient } from './client';

// ==================== Types ====================

export interface BatchListItem {
  batchId: string;
  uploadDate: string;
  /** Shift-calendar processing date (YYYY-MM-DD). Null for legacy batches uploaded before PDate was introduced. */
  pDate?: string;
  clientId: number;
  clientName: string;
  procedureCount: number;
  totalVolume: number;
  status: 'Pending' | 'InProgress' | 'Allocated' | 'PendingApproval' | 'Rejected';
  createdBy: string;
  lastModified: string;
  batchNumber: string;
  fileName: string;
}

export interface SameDayAllocationItem {
  /** HR employee code */
  employeeId: string;
  employeeName: string;
  /** Total files allocated in other batches on the same day */
  totalFiles: number;
  /** Total estimated minutes in other batches on the same day */
  estimatedMinutes: number;
  /** Batch number of the primary conflicting batch */
  batchNumber: string;
  /** Uploader name of the primary conflicting batch */
  uploaderName?: string;
}

export interface BatchAbsenceItem {
  employeeId: string;
  employeeName: string;
  markedAt: string;
  absenceType: 'FullDay' | 'HalfDay';
  /** The batch that originally created this record. Compare with current batchId to detect cross-batch rows. */
  ownerBatchId: string;
  /** Display name of the user who last marked this absence. */
  markedByName?: string;
}

export interface SetAbsencesRequest {
  employeeIds: string[];
  employeeNames: Record<string, string>;
  /** Absence type per employee. Missing = FullDay. */
  absenceTypes: Record<string, 'FullDay' | 'HalfDay'>;
}

export interface BatchListResponse {
  batches: BatchListItem[];
  total: number;
}

export interface BatchListFilters {
  status?: 'Pending' | 'InProgress' | 'Allocated' | 'PendingApproval' | 'Rejected' | 'PARTIALLY_APPROVED';
  limit?: number;
  /** Filter by specific client IDs — bypasses the uploader/hierarchy filter on the backend. */
  clientIds?: number[];
}

export interface BatchValidationCheck {
  name: string;
  isPassed: boolean;
  message: string;
  severity: 'success' | 'warning' | 'error';
}

// Rollover: procedures deferred by the allocation engine to the next working day
export interface PendingRolloverItem {
  batchProcedureId: string;
  batchId: string;
  batchNumber: string;
  clientId: number;
  procedureId: number;
  originalDailyVolume: number;
  allocatedVolume: number;
  deferredVolume: number;
  deferredReason: 'ZERO_ALLOCATION' | 'BELOW_MINIMUM' | 'PARTIAL_ALLOCATION';
  deferredAt: string;
  suggestedPriority: 'Urgent';
  allocationStatus: string;
}

export interface PendingRolloversResponse {
  totalRollovers: number;
  items: PendingRolloverItem[];
}

export interface SubmitBatchResponse {
  success: boolean;
  message: string;
  batchId?: string;
  isAutoApproved: boolean;
  validationChecks: BatchValidationCheck[];
}

export interface PendingApprovalBatch {
  batchId: string;
  batchNumber: string;
  clientName: string;
  submittedBy: string;
  submittedByEmail: string;
  submittedAt: string;
  waitingHours: number;
  procedureCount: number;          // Total SOPs in batch
  proceduresWithVolume?: number;   // SOPs that have DailyVolume > 0
  allocatedProcedures: number;     // SOPs with at least one allocation
  totalVolume: number;             // Daily volume from Excel
  totalFilesAllocated: number;     // Total files assigned to employees
  coveragePct: number;
  totalEmployees: number;
  avgUtilizationPct: number;
  employeesOverCapacity: number;
  employeesUnderUtilized: number;
  proceduresMeetingTarget: number;
  validationErrorCount: number;
  validationWarningCount: number;
  canQuickApprove: boolean;
  isAutoApproved: boolean;
  isPartiallyApproved?: boolean;
  rescheduleRequestCount?: number;   // Push-to-tomorrow request count (pending or auto/manually approved)
  /** Priority-escalation (non-reschedule) request count — includes auto-approved ones, so the manager queue can flag a priority change even when no action is required. */
  priorityChangeRequestCount?: number;
  /** Shift-calendar processing date (YYYY-MM-DD). Null for legacy batches uploaded before PDate was introduced. */
  pDate?: string | null;
}

export interface BatchReviewSummary {
  id: string;
  batchNumber: string;
  clientName: string;
  uploadedBy: string;
  uploadedByEmail: string;
  submittedAt: string | null;
  status: string;
  totalProcedures: number;
  totalVolume: number;
  totalAllocated: number;
  coveragePct: number;
  rejectionReason?: string;
  rejectedBy?: string;
  rejectedAt?: string;
  approvalComments?: string;
}

export interface ReviewEmployee {
  id: string;
  name: string;
  allocatedFiles: number;
  allocatedMinutes: number;
  capacityMinutes: number;
  utilizationPct: number;
  performanceLevel?: string;
}

export interface ReviewPriorityChange {
  requestId: string;
  action: string; // 'change' | 'reschedule'
  originalPriority: string;
  requestedPriority: string;
  justification: string;
  requestedBy: string;
  requestedAt: string;
  status: string; // 'PENDING' | 'APPROVED' | 'REJECTED'
  reviewComment?: string;
  reviewedBy?: string;
  reviewedAt?: string;
  /** For reschedule requests: encodes "Requested deferred volume: N file(s)." */
  impactDescription?: string;
}

export interface RequestDecision {
  requestId: string;
  isAccepted: boolean;
  comment?: string;
}

export interface ReviewProcedure {
  id: string;
  procedureName: string;
  priority: string;
  /** Volume as originally uploaded from Excel */
  uploadedVolume: number;
  /** Current working volume (may differ from uploadedVolume if supervisor adjusted workable) */
  volume: number;
  /** Workable volume at the time the engine last ran — min(PWM MaxVolume, uploadedVolume). Unlike `volume`, this does not shrink from later pushes/deferrals. */
  workableVolume: number;
  allocated: number;
  coveragePct: number;
  status: string;
  assignedEmployees: string[];
  manualOverrideCount: number;
  priorityChange?: ReviewPriorityChange;
  priorityChanges?: ReviewPriorityChange[];
  /** PENDING | APPROVED | HELD — set at submit time. PENDING = exception needing manager decision */
  lineStatus?: string;
  /** Manager's hold reason (only set when lineStatus = HELD) */
  holdReason?: string;
  employeeBreakdown?: Array<{
    employeeId: string;
    employeeName: string;
    allocatedFiles: number;
    estimatedMinutes: number;
  }>;
}

export interface BatchReviewDto {
  batch: BatchReviewSummary;
  validationChecks: BatchValidationCheck[];
  canApprove: boolean;
  employees: ReviewEmployee[];
  procedures: ReviewProcedure[];
}

export interface BulkApproveResponse {
  approved: string[];
  skipped: string[];
  errors: string[];
}

// ==================== Status Mapping ====================

const mapBackendStatusToFrontend = (backendStatus: string): BatchListItem['status'] => {
  const statusMap: Record<string, BatchListItem['status']> = {
    'DRAFT': 'Pending',
    'PENDING_APPROVAL': 'PendingApproval',
    'APPROVED': 'Allocated',
    'PUBLISHED': 'Allocated',
    'REJECTED': 'Rejected',
  };
  return statusMap[backendStatus.toUpperCase()] || 'Pending';
};

const mapFrontendStatusToBackend = (frontendStatus: string): string => {
  const statusMap: Record<string, string> = {
    'Pending': 'DRAFT',
    'InProgress': 'APPROVED',
    'Allocated': 'APPROVED',
    'PendingApproval': 'PENDING_APPROVAL',
    'Rejected': 'REJECTED',
    'PARTIALLY_APPROVED': 'PARTIALLY_APPROVED',
  };
  return statusMap[frontendStatus] || frontendStatus;
};

const derivePDateFromUploadDate = (uploadDate: string | undefined): string | undefined => {
  if (!uploadDate) return undefined;
  const parsed = new Date(uploadDate);
  if (Number.isNaN(parsed.getTime())) return undefined;

  // Mirror backend ComputePDate: IST (UTC+5:30), cutoff 06:00 IST.
  const ist = new Date(parsed.getTime() + (5.5 * 60 * 60 * 1000));
  const yyyyMmDd = ist.toISOString().substring(0, 10);
  const istHour = parseInt(ist.toISOString().substring(11, 13), 10);
  if (istHour < 6) {
    const prev = new Date(ist.getTime() - 24 * 60 * 60 * 1000);
    return prev.toISOString().substring(0, 10);
  }
  return yyyyMmDd;
};

// ==================== Service Methods ====================

export const batchService = {
  getBatchList: async (filters?: BatchListFilters): Promise<BatchListResponse> => {
    try {
      const params: Record<string, any> = {
        pageNumber: 1,
        pageSize: filters?.limit || 50,
        sortBy: 'UploadedAt',
        sortOrder: 'desc',
      };
      if (filters?.status) params.status = mapFrontendStatusToBackend(filters.status);
      if (filters?.clientIds && filters.clientIds.length > 0)
        params.clientIds = filters.clientIds.join(',');

      const response: any = await apiClient.get('/batches', { params });
      const batches: BatchListItem[] = response.items.map((item: any) => ({
        batchId: item.id,
        uploadDate: item.createdAt || item.batchDate,
        pDate: item.pDate ?? item.PDate ?? derivePDateFromUploadDate(item.createdAt || item.batchDate),
        clientId: item.clientId,
        clientName: item.clientName,
        procedureCount: item.totalProcedures || 0,
        totalVolume: item.totalVolume || 0,
        status: mapBackendStatusToFrontend(item.status),
        createdBy: item.createdBy || 'Unknown',
        lastModified: item.createdAt || item.batchDate,
        batchNumber: item.batchNumber,
        fileName: item.fileName || 'Unknown',
      }));

      return { batches, total: response.totalCount || batches.length };
    } catch (error: any) {
      console.error('Error fetching batch list:', error);
      throw new Error(error.response?.data?.message || 'Failed to load batches');
    }
  },

  getBatchDetails: async (batchId: string): Promise<any> => {
    try {
      return await apiClient.get(`/batches/${batchId}`, { params: { includeAllocations: false } });
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to load batch details');
    }
  },

  deleteBatch: async (batchId: string): Promise<{ success: boolean; message: string }> => {
    try {
      const response: any = await apiClient.delete(`/batches/${batchId}`);
      return { success: response.success ?? true, message: response.message || 'Batch deleted successfully' };
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to delete batch');
    }
  },

  // ── Approval workflow ──────────────────────────────────────────────────

  submitBatch: async (batchId: string): Promise<SubmitBatchResponse> => {
    try {
      const response: any = await apiClient.post(`/batches/${batchId}/submit`);
      // Success response
      return {
        success: response.success ?? true,
        message: response.message || 'Batch submitted successfully',
        batchId: response.batchId || batchId,
        isAutoApproved: response.isAutoApproved ?? false,
        validationChecks: []
      };
    } catch (error: any) {
      // Error response - the interceptor wraps it as { status, message, data }
      console.error('[batchService.submitBatch] Error caught:', error);
      
      // Extract validation details - error.data contains the actual response body
      const errorData = error.data || error.response?.data || {};
      const validationChecks = (errorData.validationChecks || []).map((check: any) => ({
        name: check.procedureName || check.name || 'Unknown Procedure',
        isPassed: check.isPassed ?? false,
        message: check.message || 'Validation failed',
        severity: (check.severity as 'success' | 'warning' | 'error') || 'error'
      }));
      
      console.log('[batchService.submitBatch] Extracted validation checks:', validationChecks);
      
      return { 
        success: false, 
        message: errorData.message || error.message || 'Failed to submit batch', 
        validationChecks 
      };
    }
  },

  getPendingApprovals: async (): Promise<PendingApprovalBatch[]> => {
    try {
      const response: any = await apiClient.get('/batches/pending-approval');
      const normalizePDate = (value: any): string | null => {
        if (!value) return null;
        const text = String(value).trim();
        // Preferred payload format from API: YYYY-MM-DD or YYYY-MM-DDTHH:mm:ss
        if (/^\d{4}-\d{2}-\d{2}/.test(text)) return text.slice(0, 10);

        // Fallback for locale-formatted strings (e.g., 6/24/2026 12:00:00 AM)
        const dt = new Date(text);
        if (!Number.isNaN(dt.getTime())) {
          const y = dt.getFullYear();
          const m = String(dt.getMonth() + 1).padStart(2, '0');
          const d = String(dt.getDate()).padStart(2, '0');
          return `${y}-${m}-${d}`;
        }
        return null;
      };

      return (response.items || response.Items || []).map((item: any) => {
        const statusRaw = item.status ?? item.Status ?? item.batchStatus ?? item.BatchStatus;
        const statusName = String(statusRaw ?? '').trim().toUpperCase();

        const autoApprovedRaw = item.isAutoApproved ?? item.IsAutoApproved;
        const parsedAutoApproved =
          typeof autoApprovedRaw === 'boolean'
            ? autoApprovedRaw
            : typeof autoApprovedRaw === 'string'
              ? autoApprovedRaw.trim().toLowerCase() === 'true'
              : autoApprovedRaw == null
                ? undefined
                : Boolean(autoApprovedRaw);

        const statusImpliesAutoApproved =
          statusRaw === 10 ||
          statusName === '10' ||
          statusName === 'AUTO_APPROVED';

        const isAutoApproved = parsedAutoApproved ?? statusImpliesAutoApproved;

        return {
          ...item,
          // Normalize to the field names consumed by ApprovalsUnified
          batchId: item.batchId ?? item.BatchId ?? '',
          batchNumber: item.batchNumber ?? item.BatchNumber ?? '',
          clientName: item.clientName ?? item.ClientName ?? '',
          submittedAt: item.submittedAt ?? item.SubmittedAt ?? '',
          isAutoApproved,
          isPartiallyApproved: item.isPartiallyApproved ?? item.IsPartiallyApproved,
          rescheduleRequestCount: item.rescheduleRequestCount ?? item.RescheduleRequestCount ?? 0,
          priorityChangeRequestCount: item.priorityChangeRequestCount ?? item.PriorityChangeRequestCount ?? 0,
          pDate: normalizePDate(item.pDate ?? item.PDate)
            ?? derivePDateFromUploadDate(item.submittedAt ?? item.SubmittedAt ?? item.createdAt ?? item.batchDate)
            ?? null,
        } as PendingApprovalBatch;
      });
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to load pending approvals');
    }
  },

  getBatchReview: async (batchId: string): Promise<BatchReviewDto> => {
    try {
      return await apiClient.get(`/batches/${batchId}/review`) as BatchReviewDto;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to load batch review');
    }
  },

  approveBatch: async (batchId: string, comments?: string): Promise<{ success: boolean; message: string }> => {
    try {
      const response: any = await apiClient.post(`/batches/${batchId}/approve`, { comments });
      return { success: response.success ?? true, message: response.message || 'Batch approved' };
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to approve batch');
    }
  },

  approveSelective: async (
    batchId: string,
    approvedProcedureIds: string[],
    heldProcedures: { procedureId: string; holdReason: string }[],
    comments?: string,
    rejectedDeferReasons?: { procedureId: string; rejectionReason: string }[],
    requestDecisions?: RequestDecision[],
  ): Promise<{ success: boolean; message: string; batchStatus?: string; approvedCount?: number; heldCount?: number }> => {
    try {
      const response: any = await apiClient.post(`/batches/${batchId}/approve-selective`, {
        approvedProcedureIds,
        heldProcedures,
        comments,
        rejectedDeferReasons: rejectedDeferReasons || [],
        requestDecisions: requestDecisions || [],
      });
      return {
        success: response.success ?? true,
        message: response.message || 'Selective approval processed',
        batchStatus: response.batchStatus,
        approvedCount: response.approvedCount,
        heldCount: response.heldCount,
      };
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to process selective approval');
    }
  },

  resubmitHeld: async (
    batchId: string,
    procedures: { procedureId: string; revisedVolume?: number; supervisorNote?: string }[],
    comments?: string,
  ): Promise<{ success: boolean; message: string; batchStatus?: string; resubmittedCount?: number; stillHeldCount?: number }> => {
    try {
      const response: any = await apiClient.post(`/batches/${batchId}/resubmit-held`, {
        procedures,
        comments,
      });
      return {
        success: response.success ?? true,
        message: response.message || 'Resubmitted successfully',
        batchStatus: response.batchStatus,
        resubmittedCount: response.resubmittedCount,
        stillHeldCount: response.stillHeldCount,
      };
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to resubmit held procedures');
    }
  },

  partialResubmit: async (
    batchId: string,
    procedureIds: string[],
    comments?: string,
  ): Promise<{ success: boolean; message: string; batchStatus?: string; autoApprovedCount?: number; sentForReviewCount?: number }> => {
    try {
      const response: any = await apiClient.post(`/batches/${batchId}/partial-resubmit`, {
        procedureIds: procedureIds.map(id => parseInt(id, 10)),
        comments,
      });
      return {
        success: response.success ?? true,
        message: response.message || 'Resubmitted successfully',
        batchStatus: response.batchStatus,
        autoApprovedCount: response.autoApprovedCount,
        sentForReviewCount: response.sentForReviewCount,
      };
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to partially resubmit procedures');
    }
  },

  rejectBatch: async (batchId: string, reason: string): Promise<{ success: boolean; message: string }> => {
    try {
      const response: any = await apiClient.post(`/batches/${batchId}/reject`, { reason });
      return { success: response.success ?? true, message: response.message || 'Batch rejected' };
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to reject batch');
    }
  },

  reopenBatch: async (batchId: string): Promise<{ success: boolean; message: string }> => {
    try {
      const response: any = await apiClient.post(`/batches/${batchId}/reopen`);
      return { success: response.success ?? true, message: response.message || 'Batch reopened' };
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to reopen batch');
    }
  },

  bulkApprove: async (batchIds: string[], comments?: string): Promise<BulkApproveResponse> => {
    try {
      const response: any = await apiClient.post('/batches/approve-bulk', { batchIds, comments });
      return response;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to bulk approve');
    }
  },

  getPendingRollovers: async (clientId?: number, days = 7): Promise<PendingRolloversResponse> => {
    try {
      const params: Record<string, string> = { days: String(days) };
      if (clientId != null) params.clientId = String(clientId);
      const response: any = await apiClient.get('/batches/pending-rollovers', { params });
      return response as PendingRolloversResponse;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch pending rollovers');
    }
  },

  getAbsences: async (batchId: string): Promise<BatchAbsenceItem[]> => {
    const response = await apiClient.get(`/batches/${batchId}/absences`) as unknown as BatchAbsenceItem[];
    return response;
  },

  getSameDayAllocations: async (batchId: string): Promise<SameDayAllocationItem[]> => {
    const response = await apiClient.get(`/batches/${batchId}/same-day-allocations`) as unknown as SameDayAllocationItem[];
    return response;
  },

  setAbsences: async (
    batchId: string,
    employeeIds: string[],
    employeeNames: Record<string, string>,
    absenceTypes: Record<string, 'FullDay' | 'HalfDay'> = {}
  ): Promise<BatchAbsenceItem[]> => {
    const response = await apiClient.post(`/batches/${batchId}/absences`, {
      employeeIds,
      employeeNames,
      absenceTypes,
    }) as unknown as BatchAbsenceItem[];
    return response;
  },

  /**
   * Marks previously-absent employee(s) present again for today's shift-day.
   * Works regardless of which batch originally marked them absent, and regardless of that
   * batch's approval status — unlike setAbsences, which only edits {batchId}'s own rows.
   */
  markPresent: async (batchId: string, employeeIds: string[]): Promise<{ removed: number }> => {
    const response = await apiClient.post(`/batches/${batchId}/absences/mark-present`, {
      employeeIds,
    }) as unknown as { removed: number };
    return response;
  },

  /**
   * Marks a single employee absent/half-day for today's shift-day. Works regardless of who owns
   * any existing record for that employee, and regardless of {batchId}'s own approval status —
   * attendance is a day-wide fact, not scoped to one batch.
   */
  markAbsent: async (
    batchId: string,
    employeeId: string,
    employeeName: string,
    absenceType: 'FullDay' | 'HalfDay' = 'FullDay'
  ): Promise<{ employeeId: string; status: string }> => {
    const response = await apiClient.post(`/batches/${batchId}/absences/mark-absent`, {
      employeeId,
      employeeName,
      absenceType,
    }) as unknown as { employeeId: string; status: string };
    return response;
  },
};

export default batchService;
